import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Readytostart from "./readytostart";
import Productinof from "./productinof";
import useTheme from "../ColorConfig/usetheme";
import SEO from "../../AASeo/Seo"; // Add SEO import

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

const ProductPageDetail = () => {
  const colors = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const selectedProductId = location.state?.selectedProductId || null;
  const passedProductsData = location.state?.productsData || null;
  const [hoveredCard, setHoveredCard] = useState(null);
  const [productsData, setProductsData] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const productRefs = useRef({});

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          "https://api.dremerz.net/api/DZ_8923_Idlbay_Products_ProductsList"
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Parse stringified JSON fields
        const parsedData = data.map((product) => ({
          ...product,
          keyFeatures: product.keyFeatures
            ? JSON.parse(product.keyFeatures)
            : [],
          benefits: product.benefits ? JSON.parse(product.benefits) : [],
          modules: product.modules ? JSON.parse(product.modules) : [],
          shortDescription: product.description,
          technologies: [],
          implementation: "Custom implementation timeline",
        }));

        setProductsData(parsedData);
        setFilteredProducts(parsedData);
        setError(null);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please try again later.");
        if (passedProductsData) {
          setProductsData(passedProductsData);
          setFilteredProducts(passedProductsData);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [passedProductsData]);

  // Fixed search functionality - only searches title and category
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts(productsData);
    } else {
      const searchTermLower = searchTerm.toLowerCase().trim();

      const filtered = productsData.filter((product) => {
        // Only search in title and category
        const titleMatch =
          product.title?.toLowerCase().includes(searchTermLower) || false;
        const categoryMatch =
          product.category?.toLowerCase().includes(searchTermLower) || false;

        return titleMatch || categoryMatch;
      });

      setFilteredProducts(filtered);
    }
  }, [searchTerm, productsData]);

  const handleProductClick = (product) => {
    navigate(`/productdetails/${product.id}`, {
      state: { productData: product },
    });
  };

  const handleSearchFromProductInfo = (query) => {
    setSearchTerm(query);

    // setTimeout(() => {
    //   const productsSection = document.querySelector('.product-grid');
    //   if (productsSection) {
    //     productsSection.scrollIntoView({ behavior: 'smooth' });
    //   }
    // }, 300);
  };

  // Add custom CSS styles
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      .product-card-container {
        perspective: 1000px;
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
        cursor: pointer;
        border-radius: 20px;
        overflow: hidden;
      }

      .product-card-container:hover {
        transform: translateY(-15px);
        z-index: 10;
      }

      .product-card {
        position: relative;
        overflow: hidden;
        border-radius: 20px;
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
        transform-style: preserve-3d;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      }

      .product-card:hover {
        box-shadow: 0 30px 60px rgba(229, 118, 39, 0.4);
      }

      .product-card-image1 {
        transition: all 0.8s cubic-bezier(0.23, 1, 0.320, 1);
        height: 180px;
        object-fit: cover;
        width: 100%;
        border-radius: 20px 20px 0 0;
      }

      .product-card:hover .product-card-image1 {
        transform: scale(1.1);
        filter: brightness(0.7);
        border-radius: 20px;
      }

      .product-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(
          135deg,
          rgba(229, 118, 39, 0.9) 0%,
          rgba(0, 0, 0, 0.8) 100%
        );
        opacity: 0;
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        padding: 2rem;
        z-index: 3;
        border-radius: 20px;
      }

      .product-card:hover .product-overlay {
        opacity: 1;
      }

      .product-content {
        transform: translateY(60px);
        transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1) 0.1s;
        opacity: 0;
      }

      .product-card:hover .product-content {
        transform: translateY(0);
        opacity: 1;
      }

      .product-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: ${colors.white};
        margin-bottom: 1rem;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .product-description {
        color: ${colors.whiteTransparent};
        line-height: 1.6;
        margin-bottom: 1.5rem;
        font-size: 0.95rem;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .productcard-title {
        color: ${colors.white};
        padding: 15px;
        font-weight: 600;
        font-size: 1.1rem;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        background: ${colors.cardBgBlack};
        border-radius: 0 0 20px 20px;
      }

      .product-features {
        margin-bottom: 1.5rem;
      }

      .product-feature-item {
        background: rgba(255, 255, 255, 0.1);
        backdrop-filter: blur(10px);
        padding: 0.5rem 1rem;
        border-radius: 25px;
        font-size: 0.8rem;
        color: ${colors.white};
        margin: 0.3rem 0.3rem 0.3rem 0;
        display: inline-block;
        border: 1px solid rgba(255, 255, 255, 0.2);
      }

      .product-btn {
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(10px);
        border: 2px solid ${colors.white};
        color: ${colors.white};
        padding: 0.8rem 2rem;
        border-radius: 30px;
        font-weight: 600;
        transition: all 0.3s ease;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-size: 0.9rem;
        cursor: pointer;
      }

      .product-btn:hover {
        background: ${colors.white};
        color: var(--color-primary);
        transform: translateY(-2px);
        box-shadow: 0 5px 15px rgba(255, 255, 255, 0.3);
      }

      .category-badge {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: linear-gradient(135deg, var(--color-primary) 0%, ${colors.primaryHover} 100%);
        color: ${colors.white};
        padding: 0.5rem 1rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        z-index: 4;
        backdrop-filter: blur(10px);
        box-shadow: 0 4px 15px rgba(229, 118, 39, 0.3);
      }

      .product-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        gap: 2rem;
        padding: 2rem 0;
      }

      .product-info-section {
        background: linear-gradient(135deg, ${colors.cardBgBlack} 0%, rgba(229, 118, 39, 0.05) 100%);
        border: 1px solid rgba(229, 118, 39, 0.2);
        border-radius: 15px;
        padding: 1rem;
        margin-bottom: 1rem;
      }

      .product-info-title {
        color: var(--color-primary);
        font-size: 0.9rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
      }

      .product-info-value {
        color: ${colors.white};
        font-weight: 700;
        font-size: 0.85rem;
      }

      .loading-container, .error-container {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 50vh;
        flex-direction: column;
      }

      .loading-spinner {
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-radius: 50%;
        border-top: 4px solid var(--color-primary);
        width: 50px;
        height: 50px;
        animation: spin 1s linear infinite;
        margin-bottom: 1rem;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }

      .search-results-info {
        color: ${colors.white};
        margin-bottom: 1rem;
        font-size: 1.1rem;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 1rem;
      }

      .clear-search {
        background: var(--color-primary);
        color: ${colors.white};
        border: none;
        padding: 0.5rem 1rem;
        border-radius: 5px;
        cursor: pointer;
        transition: all 0.3s ease;
      }

      .clear-search:hover {
        background: ${colors.primaryHover};
      }

      .no-results {
        text-align: center;
        color: ${colors.white};
        padding: 3rem;
        font-size: 1.2rem;
        grid-column: 1 / -1;
      }

      .no-results h3 {
        margin-bottom: 1rem;
        color: var(--color-primary);
      }

      .no-results p {
        margin-bottom: 1.5rem;
        opacity: 0.8;
      }

      .retry-button {
        background: var(--color-primary);
        color: ${colors.white};
        border: none;
        padding: 0.8rem 1.5rem;
        border-radius: 5px;
        cursor: pointer;
        font-weight: 600;
        margin-top: 1rem;
        transition: all 0.3s ease;
      }

      .retry-button:hover {
        background: ${colors.primaryHover};
        transform: translateY(-2px);
      }

      @media (max-width: 768px) {
        .product-grid {
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        
        .product-card-container:hover {
          transform: translateY(-10px);
        }
        
        .product-title {
          font-size: 1.2rem;
        }
        
        .productcard-title {
          font-size: 1rem;
        }
      }

      @media (min-width: 1200px) {
        .product-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }

      .pulse-animation {
        animation: pulse 2s infinite;
      }

      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }

      .floating-animation {
        animation: float 3s ease-in-out infinite;
      }

      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }

      .slide-in-animation {
        opacity: 0;
        transform: translateX(-50px);
        animation: slideIn 0.8s ease-out forwards;
      }

      @keyframes slideIn {
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (selectedProductId && selectedProductId !== "all") {
      setTimeout(() => {
        const productElement = productRefs.current[selectedProductId];
        if (productElement) {
          productElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });

          // Add highlight effect
          productElement.style.transition = "all 0.3s ease";
          productElement.style.transform = "scale(1.02)";
          productElement.style.boxShadow = `0 0 30px var(--color-primary)50`;

          setTimeout(() => {
            productElement.style.transform = "scale(1)";
            productElement.style.boxShadow = "none";
          }, 2000);
        }
      }, 100);
    }
  }, [selectedProductId, productsData]);

  const ProductCard = ({ product, index }) => {
    return (
      <div
        className={`product-card-container slide-in-animation`}
        ref={(el) => (productRefs.current[product.id] = el)}
        id={`product-${product.id}`}
        style={{
          animationDelay: `${index * 0.2}s`,
        }}
        onClick={() => handleProductClick(product)}
      >
        <div className="product-card">
          <img
            src={`https://api.dremerz.net/uploads/${product.image}`}
            alt={product.title}
            className="product-card-image1"
            onError={(e) => {
              e.target.src =
                "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop&auto=format";
            }}
          />

          <div className="category-badge">{product.category}</div>

          <div className="product-overlay">
            <div className="product-content">
              <h3 className="product-title">{product.title}</h3>
              <h2 className="product-description">{product.description}</h2>
            </div>
          </div>

          <div className="productcard-title">{product.title}</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
        className="py-1"
      >
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p style={{ color: colors.white }}>Loading products...</p>
        </div>
      </div>
    );
  }

  if (error && productsData.length === 0) {
    return (
      <div
        style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
        className="py-1"
      >
        <div className="error-container">
          <p style={{ color: colors.white }}>{error}</p>
          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ backgroundColor: colors.bodyBlack, minHeight: "100vh" }}
      className="py-1"
    >
      <SEO
        title="Our Products | IDLBAY - Software Solutions & Digital Products"
        description="Discover innovative software products and digital solutions by IDLBAY. Explore our product range for businesses and enterprises."
        keywords="IDLBAY products, software solutions, digital products, business software, enterprise software, Chennai"
        url="https://idlbay.com/products"
        organization={organization}
      />
      <div className="container">
        <Productinof onSearch={handleSearchFromProductInfo} />

        {/* Search results info */}
        {/* {searchTerm && (
          <div className="search-results-info">
            Showing {filteredProducts.length} results for "{searchTerm}"
            <button 
              className="clear-search"
              onClick={() => setSearchTerm('')}
            >
              Clear Search
            </button>
          </div>
        )} */}

        {/* Products Grid - use filteredProducts instead of productsData */}
        <div className="product-grid">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))
          ) : searchTerm ? (
            <div className="no-results">
              <h3>No products found for "{searchTerm}"</h3>
              <p>
                Try searching with different keywords or browse all products
              </p>
              <button
                className="retry-button"
                onClick={() => setSearchTerm("")}
              >
                View All Products
              </button>
            </div>
          ) : (
            productsData.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))
          )}
        </div>
      </div>
      <Readytostart />
    </div>
  );
};

export default ProductPageDetail;
